import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { CookieService } from '../services/cookie-service/cookie.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;

  constructor(
    private cookieService: CookieService,
    private router: Router,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const publicEndpoints = [
      'CompleteRegistration',
      'ResetPassword',
      'Register',
      'VerifyOTP',
      'ForgotPassword',
      'VerifyResetOTP',
      'ResendOTP',
      'ValidateToken',
      'RenvoieSMSActiverTelephone',
      'RenvoieEmailActivation',
      'wpapis/active-popup',
    ];

    const isPublicEndpoint = publicEndpoints.some((endpoint) =>
      request.url.includes(endpoint),
    );

    // withCredentials: true est nécessaire pour que le navigateur transmette le cookie
    // HttpOnly adh_auth_token sur chaque requête vers l'API.
    // Le cookie est envoyé automatiquement — plus besoin d'injecter Authorization: Bearer.
    const reqWithCredentials = request.clone({ withCredentials: true });

    return next.handle(reqWithCredentials).pipe(
      catchError((error: HttpErrorResponse) => {
        // Don't intercept 401 if it's a successful password change (requireRelogin: true)
        const isSuccessfulPasswordChange = error.status === 401 &&
                                          error.error?.success === true &&
                                          error.error?.requireRelogin === true;

        if (error.status === 401 && !isPublicEndpoint && !isSuccessfulPasswordChange) {
          this.handleLoginTokenError();
        }
        return throwError(() => error);
      }),
    );
  }

  private performSecurityCheck(
    requestUrl: string,
    token: string,
  ): { isValid: boolean; reason?: string } {
    // Check if this is a critical registration/reset endpoint
    if (
      requestUrl.includes('/CompleteRegistration') ||
      requestUrl.includes('/ResetPassword')
    ) {
      // Get token from URL
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');

      // Verify URL token exists
      if (!urlToken) {
        return {
          isValid: false,
          reason:
            'SECURITY_VIOLATION: No token found in URL for sensitive operation',
        };
      }

      // Verify tokens match
      if (urlToken !== token) {
        return {
          isValid: false,
          reason:
            'SECURITY_VIOLATION: Token mismatch detected between URL and storage',
        };
      }

      // Additional validation: Check token format
      if (!this.isValidJwtFormat(token)) {
        return {
          isValid: false,
          reason: 'SECURITY_VIOLATION: Invalid token format detected',
        };
      }
    }

    return { isValid: true };
  }

  private isValidJwtFormat(token: string): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }

      // Validate base64 encoding of header and payload
      JSON.parse(atob(parts[0]));
      JSON.parse(atob(parts[1]));

      return true;
    } catch (error) {
      return false;
    }
  }

  private handleLoginTokenError(): void {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.cookieService.removeCookies();
      this.router.navigate(['/login']).then(() => {
        // Reset flag after navigation completes
        setTimeout(() => {
          this.isRefreshing = false;
        }, 1000);
      });
    }
  }

  private isProtectedEndpoint(url: string): boolean {
    const publicEndpoints = [
      'auth/Login',
      'auth/Register',
      'auth/VerifyOTP',
      'auth/ForgotPassword',
      'auth/VerifyResetOTP',
      'auth/ResendOTP',
      'auth/ResendRegistrationOTP',
      'auth/ValidateToken',
      'auth/RenvoieSMSActiverTelephone',
      'auth/RenvoieEmailActivation',
      'auth/CompleteRegistration',
      'auth/public-key',
    ];

    return !publicEndpoints.some((endpoint) => url.includes(endpoint));
  }
}
